import { formatDistanceToNow } from "date-fns";

type DateInput = Date | string | null | undefined;

type FormatOptions = Intl.DateTimeFormatOptions & {
  fallback?: string;
  locale?: string | string[];
};

type DateTimePreset = "short" | "medium" | "long" | "chat";
type DatePreset = "short" | "medium" | "long" | "iso" | "monthDay";
type TimePreset = "short" | "medium";

const ISO_DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const ISO_DATE_TIME_REGEX = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?)(\.\d+)?([zZ]|[+-]\d{2}:?\d{2})?$/;

const normalizeTimezoneSuffix = (value?: string): string => {
  if (!value) return "Z";
  if (value === "Z" || value === "z") return "Z";
  if (/^[+-]\d{2}:\d{2}$/.test(value)) return value;
  const compact = value.match(/^([+-])(\d{2})(\d{2})$/);
  if (compact) return `${compact[1]}${compact[2]}:${compact[3]}`;
  return value;
};

const getViewerTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
};

export const parseApiInstant = (value: DateInput): Date | null => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : new Date(value.getTime());
  }
  if (value === null || value === undefined) return null;

  const raw = String(value).trim();
  if (!raw || ISO_DATE_ONLY_REGEX.test(raw)) return null;

  const isoMatch = raw.match(ISO_DATE_TIME_REGEX);
  if (isoMatch) {
    const candidate = `${isoMatch[1]}${isoMatch[2] ?? ""}${normalizeTimezoneSuffix(isoMatch[3])}`;
    const parsed = new Date(candidate);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const normalizeApiInstantString = (value: DateInput): string | null => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.toISOString();
  }
  if (value === null || value === undefined) return null;
  const raw = String(value).trim();
  if (!raw || ISO_DATE_ONLY_REGEX.test(raw)) return null;

  const isoMatch = raw.match(ISO_DATE_TIME_REGEX);
  if (isoMatch) {
    const candidate = `${isoMatch[1]}${isoMatch[2] ?? ""}${normalizeTimezoneSuffix(isoMatch[3])}`;
    return parseApiInstant(candidate) ? candidate : null;
  }

  const instant = parseApiInstant(raw);
  return instant ? instant.toISOString() : null;
};

export type ApiInstantSortParts = {
  epochMs: number;
  subMs: number;
};

export const getApiInstantSortParts = (value: DateInput): ApiInstantSortParts => {
  if (value === null || value === undefined) return { epochMs: 0, subMs: 0 };
  const raw = value instanceof Date ? value.toISOString() : String(value).trim();
  if (!raw) return { epochMs: 0, subMs: 0 };

  const isoMatch = raw.match(ISO_DATE_TIME_REGEX);
  if (isoMatch) {
    const candidate = `${isoMatch[1]}${normalizeTimezoneSuffix(isoMatch[3])}`;
    const baseMs = Date.parse(candidate);
    if (!Number.isNaN(baseMs)) {
      const fractional = (isoMatch[2] ?? "").slice(1);
      const msPart = Number((fractional + "000").slice(0, 3));
      const subMs = Number((fractional + "000000000").slice(3, 9));
      return { epochMs: baseMs + msPart, subMs };
    }
  }

  const instant = parseApiInstant(value);
  return instant ? { epochMs: instant.getTime(), subMs: 0 } : { epochMs: 0, subMs: 0 };
};

const dateTimePresets: Record<DateTimePreset, Intl.DateTimeFormatOptions> = {
  short: { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" },
  medium: { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" },
  long: {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  },
  chat: {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  },
};

const datePresets: Record<Exclude<DatePreset, "iso">, Intl.DateTimeFormatOptions> = {
  short: { month: "short", day: "numeric" },
  medium: { year: "numeric", month: "short", day: "numeric" },
  long: { year: "numeric", month: "long", day: "numeric" },
  monthDay: { month: "short", day: "numeric" },
};

const timePresets: Record<TimePreset, Intl.DateTimeFormatOptions> = {
  short: { hour: "numeric", minute: "2-digit" },
  medium: { hour: "numeric", minute: "2-digit", second: "2-digit" },
};

const formatWithIntl = (
  date: Date,
  presetOptions: Intl.DateTimeFormatOptions,
  options: FormatOptions = {}
) => {
  const { fallback: _fallback, locale, ...intlOptions } = options;
  const timeZone = intlOptions.timeZone || getViewerTimeZone();
  return new Intl.DateTimeFormat(locale || undefined, {
    ...presetOptions,
    ...intlOptions,
    ...(timeZone ? { timeZone } : {}),
  }).format(date);
};

const formatIsoDateInTimeZone = (date: Date, timeZone?: string) => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    ...(timeZone ? { timeZone } : {}),
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
};

export const formatViewerDateTime = (
  value: DateInput,
  preset: DateTimePreset = "medium",
  options: FormatOptions = {}
) => {
  const instant = parseApiInstant(value);
  if (!instant) return options.fallback ?? "—";
  return formatWithIntl(instant, dateTimePresets[preset], options);
};

export const formatViewerDate = (
  value: DateInput,
  preset: DatePreset = "medium",
  options: FormatOptions = {}
) => {
  const instant = parseApiInstant(value);
  if (!instant) return options.fallback ?? "—";
  if (preset === "iso") {
    return formatIsoDateInTimeZone(instant, options.timeZone || getViewerTimeZone());
  }
  return formatWithIntl(instant, datePresets[preset], options);
};

export const formatViewerTime = (
  value: DateInput,
  preset: TimePreset = "short",
  options: FormatOptions = {}
) => {
  const instant = parseApiInstant(value);
  if (!instant) return options.fallback ?? "—";
  return formatWithIntl(instant, timePresets[preset], options);
};

export const formatRelativeViewerTime = (
  value: DateInput,
  options: { fallback?: string; addSuffix?: boolean } = {}
) => {
  const instant = parseApiInstant(value);
  if (!instant) return options.fallback ?? "—";
  return formatDistanceToNow(instant, { addSuffix: options.addSuffix ?? true });
};

export const formatDateOnly = (
  value: DateInput,
  preset: DatePreset = "medium",
  options: FormatOptions = {}
) => {
  if (value === null || value === undefined) return options.fallback ?? "—";
  const raw = value instanceof Date ? formatIsoDateInTimeZone(value, "UTC") : String(value).trim();
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return options.fallback ?? "—";

  const isoDate = `${match[1]}-${match[2]}-${match[3]}`;
  if (preset === "iso") return isoDate;

  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return formatWithIntl(date, datePresets[preset], { ...options, timeZone: "UTC" });
};
